import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, Pencil, ShoppingBag, BedDouble, Car,
  Upload, Image as ImageIcon, X,
} from "lucide-react";
import { api } from "../../lib/api";
import { chunkedUpload, resolveUploadUrl } from "../../lib/upload";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalLabel, Badge, SectionTitle,
} from "../../components/common/Brutal";

const IMG_MAX_MB = 5;

const CATS = [
  {
    key: "hotbuys",
    label: "Hot Buys, Hot Foods",
    icon: ShoppingBag,
    endpoint: "/hotbuys",
    withGallery: false,
    empty: {
      business_name: "", business_type: "", owner: "", contact_number: "",
      facebook_page: "", logo_url: "", order: 0,
    },
    fields: [
      { key: "business_name", label: "Business Name *", required: true },
      { key: "business_type", label: "Business Type" },
      { key: "owner", label: "Owner" },
      { key: "contact_number", label: "Contact Number" },
      { key: "facebook_page", label: "Facebook Page" },
    ],
  },
  {
    key: "accommodations",
    label: "Accommodations",
    icon: BedDouble,
    endpoint: "/accommodations",
    withGallery: true,
    empty: {
      business_name: "", owner: "", contact_number: "", location: "", email: "",
      facebook_page: "", logo_url: "", gallery: [], order: 0,
    },
    fields: [
      { key: "business_name", label: "Business Name *", required: true },
      { key: "owner", label: "Owner" },
      { key: "contact_number", label: "Contact Number" },
      { key: "location", label: "Location" },
      { key: "email", label: "Email Address" },
      { key: "facebook_page", label: "Facebook Page" },
    ],
  },
  {
    key: "transport",
    label: "Transport",
    icon: Car,
    endpoint: "/transport",
    withGallery: true,
    empty: {
      business_name: "", owner: "", contact_number: "", location: "", email: "",
      facebook_page: "", logo_url: "", gallery: [], order: 0,
    },
    fields: [
      { key: "business_name", label: "Business Name *", required: true },
      { key: "owner", label: "Owner" },
      { key: "contact_number", label: "Contact Number" },
      { key: "location", label: "Location" },
      { key: "email", label: "Email Address" },
      { key: "facebook_page", label: "Facebook Page" },
    ],
  },
];

async function uploadOne(file, onPhase) {
  if (file.size > IMG_MAX_MB * 1024 * 1024) {
    toast.error(`Max ${IMG_MAX_MB}MB`);
    return null;
  }
  try {
    const res = await chunkedUpload(file, (pct, phase) => onPhase && onPhase(pct, phase));
    return res.url_path;
  } catch (e) {
    toast.error(e.message || "Upload failed");
    return null;
  }
}

function CategoryPanel({ cat }) {
  const [items, setItems] = useState([]);
  const [banner, setBanner] = useState("");
  const [form, setForm] = useState(cat.empty);
  const [editing, setEditing] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [galleryBusy, setGalleryBusy] = useState(false);
  const [bannerBusy, setBannerBusy] = useState(false);

  const load = async () => {
    const [list, banners] = await Promise.all([
      api.get(cat.endpoint),
      api.get("/experience-banners"),
    ]);
    setItems(list.data || []);
    setBanner(banners.data?.[cat.key] || "");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat.key]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name) return toast.error("Business name required");
    const payload = { ...form, order: parseInt(form.order || 0, 10) };
    if (editing) await api.put(`${cat.endpoint}/${editing}`, payload);
    else await api.post(cat.endpoint, payload);
    toast.success(editing ? `${cat.label} updated` : `${cat.label} added`);
    setEditing(null); setForm(cat.empty); load();
  };

  const edit = (b) => {
    setEditing(b.id);
    const next = { ...cat.empty };
    Object.keys(cat.empty).forEach((k) => { next[k] = b[k] ?? cat.empty[k]; });
    setForm(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`${cat.endpoint}/${id}`); load();
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setLogoBusy(true);
    const url = await uploadOne(file);
    setLogoBusy(false);
    if (url) setField("logo_url", url);
  };
  const onGallery = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setGalleryBusy(true);
    const url = await uploadOne(file);
    setGalleryBusy(false);
    if (url) setField("gallery", [...(form.gallery || []), url]);
  };
  const removeGallery = (i) => setField("gallery", form.gallery.filter((_, idx) => idx !== i));

  const onBanner = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBannerBusy(true);
    const url = await uploadOne(file);
    if (url) {
      await api.put(`/experience-banners/${cat.key}`, { banner_url: url });
      toast.success("Banner updated");
      setBanner(url);
    }
    setBannerBusy(false);
  };

  return (
    <div className="space-y-6" data-testid={`exp-admin-${cat.key}`}>
      {/* BANNER */}
      <BrutalCard className="p-5">
        <h3 className="display text-2xl mb-3 flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-[#E5247C]" />
          {cat.label} Banner
        </h3>
        {banner && (
          <div className="kf-border overflow-hidden mb-3">
            <img src={resolveUploadUrl(banner)} alt="Banner" className="w-full max-h-60 object-cover" />
          </div>
        )}
        <label className="kf-border bg-white p-3 cursor-pointer inline-flex items-center gap-2" data-testid={`${cat.key}-banner-upload`}>
          <Upload className="h-4 w-4" />
          <span className="text-xs font-bold uppercase tracking-widest">
            {bannerBusy ? "Uploading…" : banner ? "Replace banner" : "Upload banner"}
          </span>
          <input type="file" accept="image/*" className="hidden" onChange={onBanner} />
        </label>
      </BrutalCard>

      {/* FORM */}
      <BrutalCard className="p-5">
        <h3 className="display text-2xl mb-3">{editing ? "Edit Entry" : "Add Business"}</h3>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid={`${cat.key}-form`}>
          {cat.fields.map((f) => (
            <div key={f.key}>
              <BrutalLabel>{f.label}</BrutalLabel>
              <BrutalInput
                value={form[f.key] || ""}
                onChange={(e) => setField(f.key, e.target.value)}
                data-testid={`${cat.key}-input-${f.key}`}
              />
            </div>
          ))}
          <div>
            <BrutalLabel>Display Order</BrutalLabel>
            <BrutalInput type="number" value={form.order} onChange={(e) => setField("order", e.target.value)} data-testid={`${cat.key}-input-order`} />
          </div>
          <div className="sm:col-span-2">
            <BrutalLabel>Logo</BrutalLabel>
            <div className="flex items-center gap-3 flex-wrap">
              {form.logo_url && (
                <img src={resolveUploadUrl(form.logo_url)} alt="logo" className="kf-border h-20 w-20 object-cover" />
              )}
              <label className="kf-border bg-white p-3 cursor-pointer inline-flex items-center gap-2" data-testid={`${cat.key}-logo-upload`}>
                <Upload className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-widest">
                  {logoBusy ? "Uploading…" : form.logo_url ? "Replace Logo" : "Upload Logo"}
                </span>
                <input type="file" accept="image/*" className="hidden" onChange={onLogo} />
              </label>
              {form.logo_url && (
                <button type="button" onClick={() => setField("logo_url", "")} className="kf-border bg-[#EF4444] text-white px-3 py-2 text-xs font-bold uppercase tracking-widest">
                  Clear
                </button>
              )}
            </div>
          </div>

          {cat.withGallery && (
            <div className="sm:col-span-2">
              <BrutalLabel>Gallery ({(form.gallery || []).length})</BrutalLabel>
              <div className="flex flex-wrap items-start gap-3">
                {(form.gallery || []).map((g, i) => (
                  <div key={i} className="relative">
                    <img src={resolveUploadUrl(g)} alt="" className="kf-border h-20 w-20 object-cover" />
                    <button type="button" onClick={() => removeGallery(i)} className="absolute -top-2 -right-2 kf-border bg-[#EF4444] text-white p-1" data-testid={`${cat.key}-gallery-remove-${i}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="kf-border bg-white p-3 cursor-pointer inline-flex items-center gap-2" data-testid={`${cat.key}-gallery-upload`}>
                  <Plus className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase tracking-widest">
                    {galleryBusy ? "Uploading…" : "Add Photo"}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={onGallery} />
                </label>
              </div>
            </div>
          )}

          <div className="sm:col-span-2 flex gap-2">
            <BrutalButton variant="primary" type="submit" data-testid={`${cat.key}-save`}>
              {editing ? <><Save className="h-4 w-4" /> Update</> : <><Plus className="h-4 w-4" /> Add</>}
            </BrutalButton>
            {editing && (
              <BrutalButton variant="outline" type="button" onClick={() => { setEditing(null); setForm(cat.empty); }}>
                Cancel
              </BrutalButton>
            )}
          </div>
        </form>
      </BrutalCard>

      {/* LIST */}
      <BrutalCard className="p-5">
        <h3 className="display text-2xl mb-3">All ({items.length})</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`${cat.key}-list`}>
          {items.length === 0 && <div className="text-sm text-[#6B4E9E]">None yet.</div>}
          {items.map((b) => (
            <div key={b.id} className="kf-border bg-white overflow-hidden" data-testid={`${cat.key}-row-${b.id}`}>
              <div className="aspect-square bg-[#FFF4E6] overflow-hidden flex items-center justify-center">
                {b.logo_url ? (
                  <img src={resolveUploadUrl(b.logo_url)} alt={b.business_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-xs text-[#6B4E9E] uppercase">No Logo</div>
                )}
              </div>
              <div className="p-3 border-t-2 border-[#3D1E6D]">
                <div className="font-bold text-[#3D1E6D] line-clamp-1">{b.business_name}</div>
                {b.business_type && <div className="text-xs text-[#E5247C] line-clamp-1">{b.business_type}</div>}
                {b.location && <div className="text-xs text-[#E5247C] line-clamp-1">{b.location}</div>}
                {b.owner && <div className="text-xs text-[#6B4E9E] mt-1 line-clamp-1">Owner: {b.owner}</div>}
                <div className="text-[10px] uppercase tracking-widest text-[#6B4E9E] mt-1">Order: {b.order}</div>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => edit(b)} className="kf-border bg-[#FFB400] p-1.5 flex-1" data-testid={`${cat.key}-edit-${b.id}`}>
                    <Pencil className="h-3 w-3 mx-auto" />
                  </button>
                  <button onClick={() => del(b.id)} className="kf-border bg-[#EF4444] text-white p-1.5 flex-1" data-testid={`${cat.key}-del-${b.id}`}>
                    <Trash2 className="h-3 w-3 mx-auto" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </BrutalCard>
    </div>
  );
}

export default function AdminExperience() {
  const [active, setActive] = useState("hotbuys");
  const cat = CATS.find((c) => c.key === active);

  return (
    <div className="space-y-6" data-testid="admin-experience">
      <SectionTitle kicker="Experience Hub" title="Hot Buys, Accommodations & Transport" />

      <div className="flex flex-wrap gap-2 border-b-2 border-[#3D1E6D] -mx-1">
        {CATS.map((c) => {
          const Icon = c.icon;
          const isActive = active === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActive(c.key)}
              data-testid={`exp-tab-${c.key}`}
              className={`m-1 inline-flex items-center gap-2 px-4 py-2 border-2 font-bold text-xs uppercase tracking-widest ${
                isActive
                  ? "border-[#3D1E6D] bg-[#FFB400] text-[#3D1E6D] shadow-[3px_3px_0_#3D1E6D]"
                  : "border-transparent text-[#3D1E6D] hover:border-[#3D1E6D] hover:bg-[#FFF4E6]"
              }`}
            >
              <Icon className="h-4 w-4" strokeWidth={2.75} /> {c.label}
            </button>
          );
        })}
      </div>

      <div className="mt-2">
        <Badge color="magenta">{cat.label}</Badge>
        <CategoryPanel cat={cat} />
      </div>
    </div>
  );
}
